import { AxiosError } from 'axios';
import { QueryClient, useMutation, useQuery, useQueryClient } from 'react-query';

import { toast } from 'react-toastify';
import { httpClient } from 'redux/reducers/settings';
import { api, TMSResponse } from './service-api';
import TokenService, { TMSTokenDetails, TokenDetails } from './service-token';

export interface LoginDetails {
  email: string;
  password: string;
  user_type: 2;
}
export interface ForgotPassword {
  password: string;
  token: string;
  uidb64: string;
}
export interface AttendanceRequestDetails {
  reason: string;
  hall: number;
}

export interface UserOtpVerificationProps {
  otp: string;
  mobileNumber: string;
}

export interface IResetPassword {
  phoneNumber: string;
}

export interface UserSetPasswordProps {
  mobileNumber: string;
  newPassword: string;
}

export interface ForgetPassword {
  email: string;
}

type TMSUserTokenDetails = TMSTokenDetails;

export const authTokenKey = 'authToken';
const authTokenDetails = 'authTokenDetails';

const initLogout = (queryClient: QueryClient) => {
  try {
    queryClient.setQueryData(authTokenKey, () => false);
    TokenService.clearToken();
    return Promise.resolve(true);
  } catch (error) {
    console.error(error);
    return Promise.resolve(false);
  }
};

const useLogoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(() => initLogout(queryClient), {
    onSuccess: () => {
      queryClient.clear();
    }
  });
};

const initLogin = (loginData: LoginDetails) => {
  return httpClient.post<TMSResponse<TokenDetails>>(api.login, loginData, {
    disableAuth: true
  });
};

const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(initLogin, {
    onSuccess: (response) => {
      const tokenResponse = {
        access: response.data.data.access,
        refresh: response.data.data.refresh,
        isFirstLoginAttempt: response.data.first_login_attempt
      };
      TokenService.setToken(tokenResponse);
      queryClient.setQueryData(authTokenKey, () => true);
    },
    onError: (error) => {
      const loginErr = error as AxiosError<{ message: string; error: string }>;
      toast.error(
        loginErr.response?.data?.message ?? loginErr.response?.data?.error ?? 'Login failed !'
      );
    }
  });
};

const getResetTokenFunc = (token: string, uid: string) => () => {
  return httpClient.get(api.getResetToken.replace(':uid', uid).replace(':paramToken', token), {
    disableAuth: true
  });
};
const useGetResetTokenFunc = (paramToken?: string, uid?: string) => {
  return useQuery(
    [api.getResetToken, paramToken, uid],
    getResetTokenFunc(paramToken as string, uid as string),
    {
      // select: (data) => data?.data,
      enabled: !!paramToken
    }
  );
};

const forgotPassword = (loginData: ForgotPassword) => {
  return httpClient.patch<TMSResponse>(api.resetForgottenPass, loginData, {
    disableAuth: true
  });
};

const useForgotPasswordMutation = () => {
  return useMutation(forgotPassword, {
    onSuccess: (response) => {
      toast.success('Password Reset Successfully');
    },
    onError: (error) => {
      const loginErr = error as AxiosError<{ message: string; error: string }>;
      toast.error(
        loginErr.response?.data?.message ??
          loginErr.response?.data?.error ??
          'Password Reset failed !'
      );
    }
  });
};

const checkAuthentication = () => {
  if (TokenService.isAuthenticated()) {
    return Promise.resolve(true);
  }
  return Promise.reject(false);
};

/**
 * Check if user is authenticated
 * @returns boolean
 */
const useAuthentication = () => {
  const queryClient = useQueryClient();

  return useQuery(authTokenKey, checkAuthentication, {
    onSuccess: () => {
      const tokenDetails = TokenService.getTokenDetails();
      if (tokenDetails) {
        queryClient.setQueryData<TMSUserTokenDetails>(authTokenDetails, {
          ...tokenDetails
        });
      }
    }
  });
};

const useLoginTokenDetailQuery = () => {
  return useQuery<unknown, unknown, TMSUserTokenDetails>(authTokenDetails);
};

const saveUserPhoneNumber = (data: IResetPassword) => {
  return httpClient.post(api.resetPassword, data, { disableAuth: true });
};

const usePhoneNumberMutation = () => {
  return useMutation(saveUserPhoneNumber, {});
};

const userOtpValidate = (data: UserOtpVerificationProps) => {
  return httpClient.post<TMSResponse<UserOtpVerificationProps>>(api.otpVerification, data, {
    disableAuth: true
  });
};
const useOtpVerificationMutation = () => {
  return useMutation(userOtpValidate, {
    onError: (error: AxiosError) => {
      console.log(error);
    }
  });
};

const SetPasswordUser = (data: UserSetPasswordProps) => {
  return httpClient.post<TMSResponse>(api.forgetPassword, data, {
    disableAuth: true
  });
};

const useSetPasswordMutation = () => {
  return useMutation(SetPasswordUser, {
    onError: (error: AxiosError) => {
      console.log(error);
    }
  });
};

const forgetPassword = (email: ForgetPassword) => {
  const res = httpClient.post<TMSResponse>(api.sendForgetPassword, email, {
    disableAuth: true
  });
  return res;
};

const useForgetPassword = () => {
  const res = useMutation(forgetPassword, {
    onError: async (error: AxiosError) => {
      console.log(error);
      // toast.error(error.response?.data.message??'Failed to send')
    },
    onSuccess: () => {
      toast.success('check your email and verify ');
    }
  });
  return res;
};

export {
  useAuthentication,
  useForgetPassword,
  useForgotPasswordMutation,
  useGetResetTokenFunc,
  useLoginMutation,
  useLoginTokenDetailQuery,
  useLogoutMutation,
  useOtpVerificationMutation,
  usePhoneNumberMutation,
  useSetPasswordMutation
};
